import { PagingResponse } from '@/@types/responses/common/PagingResponse'
import { Supplier } from '@/@types/responses/supplier/Supplier'
import { SupplierDetails } from '@/@types/responses/supplier/SupplierDetails'
import { AppConfig } from '@/app.config'
import authenticatedFetch from '@/authenticatedFetch'
import { DropdownOptionResponse } from '@/@types/responses/common/DropdownOptionResponse'
import { DropdownOptionsResponse } from '@/@types/responses/common/DropdownOptionsResponse'
import { SupplierMessage } from '@/@types/messages/SupplierMessage'
import { ContactMessage } from '@/@types/messages/ContactMessage'
import { MiscellaneousService } from './miscellaneous.service'
import { ObjectCreatedResponse } from '@/@types/responses/common/ObjectCreatedResponse'

export class SupplierService {
  private appConfig: AppConfig
  private miscellaneousService: MiscellaneousService

  constructor(miscellaneousService: MiscellaneousService) {
    this.appConfig = new AppConfig()
    this.miscellaneousService = miscellaneousService
  }

  public createSupplier(supplierMessage: SupplierMessage): Promise<ObjectCreatedResponse<number>> {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(supplierMessage)
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/suppliers`, this.miscellaneousService, requestOptions).then(async response => {
      const json = await response.json()
      return new ObjectCreatedResponse<number>(json.id)
    })
  }

  public createSupplierContact(supplierId: number, contactMessage: ContactMessage): Promise<ObjectCreatedResponse<number>> {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(contactMessage)
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/suppliers/${supplierId}/contacts`, this.miscellaneousService, requestOptions).then(async response => {
      const json = await response.json()
      return new ObjectCreatedResponse<number>(json.id)
    })
  }

  public deleteSupplier(id: number): Promise<void> {
    const requestOptions = {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' }
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/suppliers/${id}`, this.miscellaneousService, requestOptions).then(() => {
      return Promise.resolve()
    })
  }

  public getSupplier(id: number): Promise<SupplierDetails> {
    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/suppliers/${id}`, this.miscellaneousService).then(async response => {
      const json = await response.json()
      return new SupplierDetails(json.id, json.name, json.website, json.email, json.phoneNumber, json.addressLine1, json.addressLine2, json.city, json.stateId, json.postalCode, json.contacts, json.inventoryTypeIds, json.createdByUser, json.createdDate, json.lastModifiedByUser, json.lastModifiedDate)
    })
  }

  public getSupplierDropdownOptions(): Promise<DropdownOptionsResponse<DropdownOptionResponse<number>>> {
    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/suppliers/dropdown-options`, this.miscellaneousService).then(async response => {
      const json = await response.json()
      return json as DropdownOptionsResponse<DropdownOptionResponse<number>>
    })
  }

  public searchSuppliers(searchTerm: string, pageNumber: number, pageSize: number, sortField: string, ascending: boolean): Promise<PagingResponse<Supplier>> {
    const queryString = `?searchTerm=${searchTerm}&pageNumber=${pageNumber}&pageSize=${pageSize}&sortField=${sortField}&ascending=${ascending}`

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/suppliers${queryString}`, this.miscellaneousService).then(async response => {
      const json = await response.json()
      return json as PagingResponse<Supplier>
    })
  }

  public updateSupplier(supplierId: number, supplierMessage: SupplierMessage): Promise<void> {
    const requestOptions = {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(supplierMessage)
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/suppliers/${supplierId}`, this.miscellaneousService, requestOptions).then(() => {
      return Promise.resolve()
    })
  }
}
